import { logger } from "@/utils/logger";

const API_BASE = "/api";
const DEFAULT_TIMEOUT_MS = 15000;

// Refresh token lock — prevents concurrent refresh attempts from racing
let refreshPromise = null;

function fetchWithTimeout(url, options, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal }).finally(() =>
    clearTimeout(id)
  );
}

async function requestWithAuth(url, options = {}, extraHeaders = {}) {
  let token = localStorage.getItem("token");
  const refreshToken = localStorage.getItem("refresh_token");

  // Timeout configurável por chamada (default 15s). Jobs longos (scrape de
  // CSV de 11 unidades com auto-rotação) passam um timeout maior pra não
  // abortar o fetch no meio. `timeoutMs` não é prop de RequestInit — extraímos
  // (e removemos de `options`) para repassar ao fetchWithTimeout sem vazar no fetch().
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  delete options.timeoutMs;

  // Verifica se é FormData - não deve definir Content-Type (browser faz automaticamente)
  const isFormData = options.body instanceof FormData;

  const defaultHeaders = {
    Authorization: `Bearer ${token}`,
    "PTM-USE-CACHE": "true",
    "PTM-Client": "WebAdmin",
    "PTM-Platform": "Web",
  };

  // Só adiciona Content-Type se não for FormData
  if (!isFormData) {
    defaultHeaders["Content-Type"] = "application/json";
  }

  options.headers = {
    ...defaultHeaders,
    ...extraHeaders,
    ...options.headers,
  };

  let response = await fetchWithTimeout(API_BASE + url, options, timeoutMs);

  if (response.status === 401 && refreshToken) {
    const refreshed = await tryRefreshTokenOnce(token, refreshToken);

    if (refreshed) {
      // tenta de novo com novo token
      token = localStorage.getItem("token");
      options.headers.Authorization = `Bearer ${token}`;
      response = await fetchWithTimeout(API_BASE + url, options, timeoutMs);
    } else {
      // refresh falhou, desloga
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
      window.location.href = "/";
      throw new Error("Session expired");
    }
  }

  return response;
}

/**
 * Ensures only one refresh token request runs at a time.
 * Concurrent callers wait for the same promise.
 */
async function tryRefreshTokenOnce(token, refreshToken) {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = tryRefreshToken(token, refreshToken);

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

async function tryRefreshToken(token, refreshToken) {
  try {
    const res = await fetch(`${API_BASE}/auth/v1/signin/refresh`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "PTM-Client": "WebAdmin",
        "PTM-Platform": "Web",
      },
      body: JSON.stringify({
        token: token,
        refresh_token: refreshToken,
      }),
    });

    if (!res.ok) return false;

    const data = await res.json();

    localStorage.setItem("token", data.token);
    localStorage.setItem("refresh_token", data.refresh_token);
    return true;
  } catch (err) {
    logger.error("Erro no refresh token:", err);
    return false;
  }
}

export { requestWithAuth };
